import type { FC } from "react";
import { Button } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { SimpleTooltip } from "../tooltip/simpleTooltip.js";
import { TooltipTrigger } from "../tooltip/tooltipTrigger.js";

export interface StackedBarSegment {
  color: string;
  /**
   * Optional outline color drawn around the segment as an inset box-shadow.
   * Use together with a lighter `color` fill to produce a soft pastel bar
   * with a crisp darker outline.
   */
  strokeColor?: string;
  name: string;
  count: number;
  percent: number;
}

export interface StackedBarProps {
  segments: readonly StackedBarSegment[];
  className?: string;
  /**
   * When true (default), each segment shows its rounded percentage inline when
   * wide enough.
   */
  showSegmentLabels?: boolean;
  /**
   * Optional click handler. When provided each segment becomes a real button
   * (hover cursor, keyboard activation, `aria-pressed` when active).
   */
  onSegmentClick?: (index: number) => void;
  /**
   * Index of the segment to render as the currently-selected one. When
   * set, that segment stays at full opacity and the rest dim.
   */
  activeIndex?: number | null;
}

/**
 * Horizontal bar divided into colored segments proportional to each value's percentage.
 * Each segment shows its percentage inline (when wide enough) and a tooltip with the
 * name, count, and percentage on hover.
 */
export const StackedBar: FC<StackedBarProps> = ({
  segments,
  className,
  showSegmentLabels = true,
  onSegmentClick,
  activeIndex = null,
}) => {
  const total = segments.reduce((sum, s) => sum + s.count, 0);
  const isClickable = onSegmentClick != null;

  return (
    <div className={twMerge("flex h-6 overflow-hidden rounded-sm", className)}>
      {total === 0 ? (
        <div className="h-full w-full bg-transparent" />
      ) : (
        segments.map((seg, i) => {
          const wholePct = Math.round(seg.percent);
          if (seg.percent === 0) return null;
          const isActive = activeIndex === i;
          const isDimmed = isClickable && activeIndex !== null && !isActive;
          return (
            <TooltipTrigger key={i}>
              <Button
                onPress={isClickable ? () => onSegmentClick(i) : undefined}
                aria-pressed={isClickable ? isActive : undefined}
                className={twMerge(
                  "flex h-full items-center justify-center text-xs font-medium text-foreground outline-hidden transition-opacity",
                  isClickable && "cursor-pointer hover:opacity-80",
                  isDimmed && "opacity-50",
                )}
                style={{
                  width: `${wholePct}%`,
                  minWidth: "4px",
                  backgroundColor: seg.color,
                  boxShadow: seg.strokeColor
                    ? `inset 0 0 0 1px ${seg.strokeColor}`
                    : undefined,
                }}
              >
                {showSegmentLabels && seg.percent > 8 ? `${wholePct}%` : null}
              </Button>
              <SimpleTooltip>
                {seg.name}: {seg.count} (
                {wholePct === 0 && seg.count > 0 ? "<1" : wholePct}%)
              </SimpleTooltip>
            </TooltipTrigger>
          );
        })
      )}
    </div>
  );
};
